import { DISCOVER_GAMES_BULK_SELECT_KEY } from "../../../../datagrid-configs/discoverGames/constants";
import type { GameRow } from "../../../../datagrid-configs/discoverGames/types";
import type { ColumnConfig, ColumnVisibility } from "../../../../datagrid-configs/types";

/**
 * Merge a persisted column order with the current config.
 * Unknown saved keys are dropped; newly added config keys are inserted next to
 * their config neighbors (not appended at the end of a wide table).
 */
export function mergeColumnOrder(configKeys: string[], savedOrder: string[]): string[] {
	const configKeySet = new Set(configKeys);
	const mergedKeys = savedOrder.filter((key) => configKeySet.has(key));

	configKeys.forEach((key, index) => {
		if (mergedKeys.includes(key)) {
			return;
		}
		let insertAt = 0;
		for (let predecessorIndex = index - 1; predecessorIndex >= 0; predecessorIndex--) {
			const savedPredecessorIndex = mergedKeys.indexOf(configKeys[predecessorIndex]);
			if (savedPredecessorIndex !== -1) {
				insertAt = savedPredecessorIndex + 1;
				break;
			}
		}
		mergedKeys.splice(insertAt, 0, key);
	});

	return mergedKeys;
}

/** Fill in newly added columns using their default visibility. Saved values win. */
export function mergeColumnVisibility(
	defaultVisibility: ColumnVisibility,
	savedVisibility: ColumnVisibility,
): ColumnVisibility {
	return { ...defaultVisibility, ...savedVisibility };
}

/** Order columns per saved order, insert any missing from config, pin bulk-select first. */
export function orderColumnsForDisplay(
	discoverGamesColumns: ColumnConfig<GameRow>[],
	columnOrder: string[],
): ColumnConfig<GameRow>[] {
	const columnMap = new Map<string, ColumnConfig<GameRow>>();
	discoverGamesColumns.forEach((col) => {
		columnMap.set(String(col.key), col);
	});

	const mergedKeys = mergeColumnOrder(
		discoverGamesColumns.map((col) => String(col.key)),
		columnOrder,
	);
	const ordered: ColumnConfig<GameRow>[] = [];

	mergedKeys.forEach((key) => {
		const col = columnMap.get(key);
		if (col !== undefined) {
			ordered.push(col);
		}
	});

	const bulkIdx = ordered.findIndex((c) => String(c.key) === DISCOVER_GAMES_BULK_SELECT_KEY);
	if (bulkIdx > 0) {
		const [bulkCol] = ordered.splice(bulkIdx, 1);
		ordered.unshift(bulkCol);
	}

	return ordered;
}
